import { useQuery } from '@tanstack/react-query';
import { credentialsApi } from '@/api/credentials';
import { queryKeys } from '@/lib/queryKeys';
import { useApiMutation, type MutationCallbacks } from '@/hooks/useApiMutation';
import type { CreateCredentialRequest, CredentialOwner, SiteCredentialView, UpdateCredentialRequest } from '@/types/api';

const keyOf = (owner: CredentialOwner) => (owner.kind === 'site' ? queryKeys.siteCredentials(owner.id) : queryKeys.nodeCredentials(owner.id));

/** A site's or a server's credentials, for those who may see them; nothing is asked for otherwise. */
export function useCredentials(owner: CredentialOwner, enabled: boolean) {
  return useQuery({ queryKey: keyOf(owner), queryFn: () => credentialsApi.list(owner), enabled });
}

/** A site's credentials. */
export function useSiteCredentials(siteId: string, enabled: boolean) {
  return useCredentials({ kind: 'site', id: siteId }, enabled);
}

export function useCreateCredential(owner: CredentialOwner, callbacks?: MutationCallbacks<SiteCredentialView, CreateCredentialRequest>) {
  return useApiMutation((input: CreateCredentialRequest) => credentialsApi.create(owner, input), () => [keyOf(owner)], callbacks);
}

export function useUpdateCredential(
  owner: CredentialOwner,
  callbacks?: MutationCallbacks<SiteCredentialView, { id: string; input: UpdateCredentialRequest }>,
) {
  return useApiMutation(
    ({ id, input }: { id: string; input: UpdateCredentialRequest }) => credentialsApi.update(owner, id, input),
    () => [keyOf(owner)],
    callbacks,
  );
}

export function useDeleteCredential(owner: CredentialOwner, callbacks?: MutationCallbacks<{ removed: boolean }, string>) {
  return useApiMutation((id: string) => credentialsApi.remove(owner, id), () => [keyOf(owner)], callbacks);
}

/**
 * Signing into the site's admin with a kept CMS login (ADR-0035). The tab is
 * opened at the click, while the browser still takes it for the user's doing —
 * one opened when the answer arrives would be stopped as a pop-up — and sent
 * to the one-time page once the panel has handed it out.
 */
export function useCmsSignIn(siteId: string, callbacks?: MutationCallbacks<{ url: string }, string>) {
  const mutation = useApiMutation((id: string) => credentialsApi.signIn(siteId, id), () => [queryKeys.siteCredentials(siteId)], callbacks);
  return {
    busy: mutation.isPending,
    signIn(credentialId: string): void {
      const tab = window.open('', '_blank');
      // The site's pages get no handle on the panel's tab.
      if (tab) tab.opener = null;
      mutation.mutateAsync(credentialId).then(
        ({ url }) => {
          if (tab) tab.location.replace(url);
          else window.open(url, '_blank', 'noopener');
        },
        () => tab?.close(),
      );
    },
  };
}

/** The password of one record. Refreshes the list after, for "last shown by". */
export function useRevealCredential(owner: CredentialOwner, callbacks?: MutationCallbacks<{ password: string }, string>) {
  return useApiMutation((id: string) => credentialsApi.reveal(owner, id), () => [keyOf(owner)], callbacks);
}
